import { CheckCircle2, Clock, AlertTriangle, Ban } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const CLOSED_STATUSES = new Set([
  "Enrolled",
  "Closed-lost",
  "Closed",
  "Wrong number",
]);

const COOLDOWN_MIN = 60; // minutes considered "still cooling down after a recent contact"

function minutesSince(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 60000));
}

/**
 * Determine the contact policy for a lead.
 * Priority: Closed > Paused > Overdue > Wait > Ready
 */
export function policyFor(lead) {
  if (!lead) return { kind: "ready" };
  const status = lead.status || "";
  if (CLOSED_STATUSES.has(status)) {
    return { kind: "closed", label: "Closed", tone: "slate", reason: status };
  }
  const attempt = lead.attempt_status || {};
  if (attempt.status === "paused" || lead.cadence_paused) {
    return { kind: "closed", label: "Paused", tone: "slate", reason: "Cadence paused" };
  }
  const minsLast = minutesSince(lead.last_contacted_at);
  const behind = Number(attempt.behind_total || 0);
  if (behind > 0 && (minsLast === null || minsLast >= COOLDOWN_MIN)) {
    // Prefer server-computed breach reason (richer than generic count)
    const richReason = attempt.reason;
    const sev = attempt.severity || "low";
    return {
      kind: "overdue",
      label: "Overdue",
      tone: sev === "high" ? "rose" : sev === "med" ? "amber" : "rose",
      reason:
        richReason || `${behind} attempt${behind > 1 ? "s" : ""} behind SLA`,
      severity: sev,
      hours_behind: attempt.hours_behind || 0,
    };
  }
  if (minsLast !== null && minsLast < COOLDOWN_MIN) {
    return {
      kind: "wait",
      label: "Wait",
      tone: "amber",
      reason: `Contacted ${minsLast}m ago — let the lead breathe`,
    };
  }
  return {
    kind: "ready",
    label: "Ready",
    tone: "emerald",
    reason: minsLast !== null ? `Last contact ${minsLast}m ago` : "Never contacted",
  };
}

const TONE = {
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-300",
  amber: "bg-amber-100 text-amber-800 border-amber-300",
  rose: "bg-rose-100 text-rose-800 border-rose-300",
  slate: "bg-slate-100 text-slate-600 border-slate-300",
};

const ICONS = {
  ready: CheckCircle2,
  wait: Clock,
  overdue: AlertTriangle,
  closed: Ban,
};

export default function ContactPolicyBadge({ lead, compact = false }) {
  const p = policyFor(lead);
  const Icon = ICONS[p.kind] || CheckCircle2;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${TONE[p.tone] || TONE.slate}`}
            data-testid={`policy-badge-${p.kind}`}
          >
            <Icon className="h-3 w-3" strokeWidth={2.2} />
            {!compact && p.label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[200px] text-xs">
          <div className="font-semibold capitalize">{p.label}</div>
          <div className="mt-0.5 text-slate-300">{p.reason}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
